import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../constants/api';
import ContactLogo from '../components/shared/ContactLogo';
import { usePlan } from '../contexts/PlanContext';
import { trackPurchase } from '../lib/metaPixel';

/* Return page for the recurring-subscription checkout (see
   subscriptions.py / PlansCompareModal.js) — separate from
   PaymentSuccessPage.js, which is the physical-card order confirmation
   and shows shipping/order details that don't apply here. Doesn't wait on
   MercadoPago's webhook: fires POST /subscriptions/sync/{preapproval_id}
   the instant this page loads, which actively checks MercadoPago's real
   status right there instead of depending on notification delivery timing
   — the webhook and the scheduled sweep in server.py remain as a fallback
   for whoever never lands on this page. refreshPlan() below then just
   re-reads our own already-updated plan. */
const SubscriptionSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshPlan, plan } = usePlan();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const preapprovalId = searchParams.get('preapproval_id');

    const syncNow = async () => {
      if (!preapprovalId) return;
      try {
        const token = localStorage.getItem('token');
        await axios.post(
          `${API}/subscriptions/sync/${preapprovalId}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        // Ignored — the polling loop below and the scheduled backend sweep
        // are the fallback if this immediate sync doesn't succeed.
      }
    };

    let attempts = 0;
    const poll = async () => {
      await refreshPlan();
      attempts += 1;
      if (cancelled) return;
      if (attempts >= 5) {
        setChecking(false);
        return;
      }
      setTimeout(poll, 2000);
    };

    syncNow().then(poll);

    return () => { cancelled = true; };
  }, []);

  const isPro = plan?.is_pro;
  const preapprovalId = searchParams.get('preapproval_id');

  // Purchase fires only once isPro flips true from the polling above (a
  // real, backend-confirmed activation — never just "the user is on this
  // URL") and only when preapproval_id is present, so a Pro user who
  // revisits this page later for any reason never re-triggers it. The
  // real charged amount comes straight from the subscription record
  // rather than being guessed from the plan's list price, since a
  // discount/admin override could differ from it.
  useEffect(() => {
    if (!isPro || !preapprovalId) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/subscriptions/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (res.data?.amount_clp != null) {
          trackPurchase({
            transactionId: preapprovalId,
            value: res.data.amount_clp,
            currency: 'CLP',
            contentId: 'pro_plan',
          });
        }
      } catch (err) {
        // If this fetch fails the Purchase event simply doesn't fire —
        // safer than tracking a guessed amount that might not match what
        // was actually charged.
      }
    })();
    return () => { cancelled = true; };
  }, [isPro, preapprovalId]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        <div className="mb-6 flex justify-center"><ContactLogo className="h-10 w-auto" /></div>

        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>

        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          {isPro ? '¡Ya eres Pro!' : 'Suscripción en proceso'}
        </h1>

        <p className="text-gray-600 mb-6 leading-relaxed">
          {isPro
            ? 'Tu suscripción fue activada. Ya puedes usar todas las funciones de tu nuevo plan.'
            : checking
              ? 'MercadoPago está confirmando tu pago. Esto suele tardar unos segundos.'
              : 'Tu pago fue recibido. Tu plan Pro se activará dentro de los próximos minutos — si pasa más de eso, escríbenos.'}
        </p>

        {!isPro && checking && (
          <div className="flex justify-center mb-6">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0071e3]" />
          </div>
        )}

        <button
          onClick={() => navigate('/dashboard')}
          className="w-full bg-black hover:opacity-90 text-white py-3 rounded-full font-medium transition"
        >
          Ir a mi cuenta
        </button>
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;
